import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { BiDollar, BiGlobe, BiPackage, BiPlus, BiRocket, BiStore, BiVideo } from 'react-icons/bi';
import { LuAlarmClock } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { resetCreateFlow, setType, toggeleCreateModal } from '@/features/scenario/create/create.slice';
import usePermission from '@/hooks/usePermission';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

function MarketplaceEmptyState() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const permissions = usePermission();
  const marketplacePermission = permissions.menu.marketplace;

  const handleCreateNewItem = () => {
    dispatch(resetCreateFlow());
    dispatch(toggeleCreateModal(true));
    dispatch(setType(BotTypeEnum.MARKETPLACE));
  };

  const handleRequestMarketplaceAccess = () => {
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/contact`);
  };

  const features = [
    {
      icon: BiPackage,
      title: t('marketplace/promotion/features/chatbotPackages/title', 'Chatbot Packages'),
      description: t(
        'marketplace/promotion/features/chatbotPackages/description',
        'Create and sell complete chatbot solutions with pre-configured scenarios, knowledge bases, and conversation flows.'
      ),
      price: t('marketplace/promotion/features/chatbotPackages/price', '$10 - $500'),
      color: 'blue',
    },
    {
      icon: BiVideo,
      title: t('marketplace/promotion/features/videoPackages/title', 'Video Tutorial Packages'),
      description: t(
        'marketplace/promotion/features/videoPackages/description',
        'Share your expertise through comprehensive video tutorials that teach others how to build and optimize chatbots.'
      ),
      price: t('marketplace/promotion/features/videoPackages/price', '$20 - $200'),
      color: 'purple',
    },
  ];

  const benefits = [
    {
      icon: BiDollar,
      title: t('marketplace/promotion/benefits/monetize/title', 'Monetize Your Expertise'),
      description: t('marketplace/promotion/benefits/monetize/description', 'Turn your chatbot building skills into a revenue stream'),
    },
    {
      icon: BiRocket,
      title: t('marketplace/promotion/benefits/grow/title', 'Grow Your Business'),
      description: t('marketplace/promotion/benefits/grow/description', 'Reach thousands of potential customers instantly'),
    },
    {
      icon: BiGlobe,
      title: t('marketplace/promotion/benefits/global/title', 'Global Reach'),
      description: t('marketplace/promotion/benefits/global/description', 'Access an international marketplace of buyers'),
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'tw-bg-blue-100 tw-text-blue-600';
      case 'purple':
        return 'tw-bg-purple-100 tw-text-purple-600';
      case 'green':
        return 'tw-bg-green-100 tw-text-green-600';
      default:
        return 'tw-bg-gray-100 tw-text-gray-600';
    }
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-py-12 tw-px-6">
      {/* Hero Section */}
      <div className="tw-text-center tw-mb-16">
        <div className="tw-relative tw-mb-8">
          <div className="tw-w-32 tw-h-32 tw-rounded-full tw-bg-gradient-to-br tw-from-blue-500 tw-to-purple-600 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-shadow-2xl">
            <BiStore className="tw-w-16 tw-h-16 tw-text-white" />
          </div>
          <div className="tw-absolute tw-top-0 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-2">
            <div className="tw-bg-yellow-400 tw-text-yellow-900 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-shadow-lg">
              {t('marketplace/promotion/badge', 'NEW')}
            </div>
          </div>
        </div>

        <h1 className="tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-6">{t('marketplace/promotion/hero/title', 'Welcome to the LouiseBot Marketplace')}</h1>

        <p className="tw-text-xl tw-text-gray-600 tw-mb-8 tw-max-w-3xl tw-mx-auto">
          {t(
            'marketplace/promotion/hero/subtitle',
            'Create, sell, and share your chatbot expertise with thousands of users. Start earning revenue from your chatbot creations today.'
          )}
        </p>

        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center tw-mb-12">
          {marketplacePermission ? (
            <div className="tw-flex tw-flex-col tw-gap-5  tw-items-center">
              {/* Prominent Plus Button */}
              <div
                className="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-rounded-full tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-flex tw-items-center tw-justify-center tw-shadow-xl sm:tw-shadow-2xl tw-cursor-pointer tw-transition-all tw-duration-300 hover:tw-scale-110 hover:tw-shadow-2xl sm:hover:tw-shadow-3xl hover:tw-from-blue-600 hover:tw-to-blue-700 tw-group tw-relative tw-overflow-hidden"
                onClick={handleCreateNewItem}
                onKeyDown={e => e.key === 'Enter' && handleCreateNewItem()}
                role="button"
                tabIndex={0}
              >
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-white/0 tw-to-white/30 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300" />
                <BiPlus className="tw-w-8 tw-h-8 sm:tw-w-10 sm:tw-h-10 tw-text-white tw-transition-transform tw-duration-300 group-hover:tw-rotate-90 tw-relative tw-z-10" />
              </div>

              {/* Main CTA Button */}
              <PSButton
                id="btn-cta-create"
                onClick={handleCreateNewItem}
                className="tw-group tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 hover:tw-from-blue-700 hover:tw-to-blue-800 tw-border-none tw-text-white tw-px-8 sm:tw-px-12 tw-py-4 sm:tw-py-5 tw-rounded-xl sm:tw-rounded-2xl tw-transition-all tw-duration-300 tw-shadow-lg sm:tw-shadow-xl hover:tw-shadow-xl sm:hover:tw-shadow-2xl hover:tw-scale-105 tw-text-lg sm:tw-text-xl tw-font-bold tw-transform tw-relative tw-overflow-hidden tw-w-full sm:tw-w-auto tw-max-w-xs sm:tw-max-w-none"
              >
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-white/0 tw-to-white/20 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300" />
                <span className="tw-relative tw-tracking-wide tw-text-center">{t('marketplace/empty/cta', 'Create Your First Package')}</span>
              </PSButton>
            </div>
          ) : (
            <PSButton
              id="btn-promotion-request"
              onClick={handleRequestMarketplaceAccess}
              className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-border-none tw-text-white tw-px-8 tw-py-4 tw-rounded-xl hover:tw-from-blue-700 hover:tw-to-purple-700 tw-transition-all tw-duration-200 tw-shadow-lg hover:tw-shadow-xl tw-flex tw-items-center tw-gap-3 tw-font-medium tw-text-lg"
            >
              <LuAlarmClock className="tw-w-6 tw-h-6" />
              <u>{t('marketplace/promotion/hero/limited', 'Limited Offer!')}</u>
              {t('marketplace/promotion/hero/cta', 'Request Access Now')}
            </PSButton>
          )}
        </div>
      </div>

      {/* Package Types */}
      <div className="tw-mb-16">
        <h2 className="tw-text-3xl tw-font-bold tw-text-center tw-text-gray-900 tw-mb-12">{t('marketplace/promotion/packages/title', 'What Can You Sell?')}</h2>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-p-8 tw-border tw-border-gray-100 hover:tw-shadow-xl tw-transition-all tw-duration-300 tw-flex tw-flex-col tw-h-full"
            >
              <div className={`tw-w-16 tw-h-16 tw-rounded-2xl ${getColorClasses(feature.color)} tw-flex tw-items-center tw-justify-center tw-mb-6`}>
                <feature.icon className="tw-w-8 tw-h-8" />
              </div>

              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4">{feature.title}</h3>

              <p className="tw-text-gray-600 tw-mb-6 tw-leading-relaxed tw-flex-grow">{feature.description}</p>

              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-mt-auto">
                <div className="tw-text-sm tw-text-gray-500 tw-mb-1">{t('marketplace/promotion/packages/priceRange', 'Typical Price Range')}</div>
                <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{feature.price}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue Model */}
      <div className="tw-bg-gradient-to-r tw-from-green-50 tw-to-blue-50 tw-rounded-3xl tw-p-8 tw-mb-16">
        <div className="tw-text-center tw-mb-8">
          <h2 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-4">{t('marketplace/promotion/revenue/title', 'Transparent Revenue Sharing')}</h2>
          <p className="tw-text-gray-600 tw-text-lg">{t('marketplace/promotion/revenue/subtitle', 'We believe in fair compensation for creators')}</p>
        </div>

        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-start tw-justify-center tw-gap-8">
          <div className="tw-text-center tw-flex tw-flex-col tw-items-center">
            <div className="tw-w-24 tw-h-24 tw-rounded-full tw-bg-green-500 tw-flex tw-items-center tw-justify-center tw-mb-4 tw-relative tw-overflow-hidden tw-border-4 tw-border-yellow-400 tw-shadow-lg tw-animate-glow">
              <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-green-300 tw-to-transparent tw-opacity-80 tw-transform -tw-skew-x-12 tw-animate-shine"></div>
              <span className="tw-text-2xl tw-font-bold tw-text-white tw-relative tw-z-10">70%</span>
            </div>
            <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">{t('marketplace/promotion/revenue/creator', 'For You')}</h3>
            <p className="tw-text-gray-600">{t('marketplace/promotion/revenue/creatorDesc', 'Keep the majority of your earnings')}</p>
          </div>

          <div className="tw-flex tw-items-center tw-justify-center">
            <div className="tw-w-16 tw-h-1 tw-bg-gray-300 tw-rounded-full md:tw-w-1 md:tw-h-16"></div>
          </div>

          <div className="tw-text-center tw-flex tw-flex-col tw-items-center">
            <div className="tw-w-24 tw-h-24 tw-rounded-full tw-bg-white tw-flex tw-items-center tw-justify-center tw-mb-4 tw-border-4 tw-border-blue-500 tw-shadow-lg">
              <span className="tw-text-2xl tw-font-bold tw-text-blue-500">30%</span>
            </div>
            <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">{t('marketplace/promotion/revenue/platform', 'Platform')}</h3>
            <p className="tw-text-gray-600">
              {ReactHtmlParser(t('marketplace/promotion/revenue/platformDesc', 'Covers hosting, payment<br />processing, and support'))}
            </p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="tw-mb-16">
        <h2 className="tw-text-3xl tw-font-bold tw-text-center tw-text-gray-900 tw-mb-12">
          {t('marketplace/promotion/benefits/title', 'Why Sell on LouiseBot?')}
        </h2>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="tw-text-center">
              <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
                <benefit.icon className="tw-w-8 tw-h-8 tw-text-gray-600" />
              </div>

              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4">{benefit.title}</h3>

              <p className="tw-text-gray-600 tw-leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WrappedErrorBoundary(MarketplaceEmptyState);
